import type { SyntheticEvent } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import { PROOF_FILE } from "consts/ecommerce";
import { getFormInputProps } from "util/forms";
import { adultDate } from "util/support";
import { UiDateTimeFormatter } from "util/formaters";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import OnboardingForm from "ui/forms/ECommerce/Onboarding/OnboardingForm";
import { ProofFileInput, acceptRules, IdentityPlaceholderTitle } from "ui/forms/formik-elements/FileInput";
import { FormField, type OnboardingDirectorsAndShareholdersProps } from "./types";
import OtherShareholders from "./OtherShareholders";

const DirectorsAndShareholders = (onboardingDirectorsAndShareholdersProps: OnboardingDirectorsAndShareholdersProps) => {
    const {
        values,
        isSubmitting,
        isValid,
        isBootstrapDataLoading,
        isIncomplete,
        requestedChangeFields,
        bootstrapData,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit,
        ...form
    } = onboardingDirectorsAndShareholdersProps;

    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h5'
                gutterBottom
            >
                Director / CEO
            </Typography>
            <Typography
                variant='h6'
            >
                Upload Passport
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Passport should be in date and within 3 months of expiry
            </Typography>
            <OnboardingInputControl
                name={FormField.CeoPassportFileKey}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.CeoPassportFileKey}
                        file={values[FormField.CeoPassportFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                Upload Utility Bill
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Dated within the previous 3 months
            </Typography>
            <OnboardingInputControl
                name={FormField.CeoUtilityBillFileKey}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.CeoUtilityBillFileKey}
                        file={values[FormField.CeoUtilityBillFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Personal Information
            </Typography>
            <OnboardingInputControl
                name={FormField.CeoName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CeoName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.CeoName}
                        value={values[FormField.CeoName]}
                        label='Full Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CeoDateOfBirth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            disabled={disabled}
                            maxDate={adultDate()}
                            format={UiDateTimeFormatter.Ui}
                            label='Date of Birth'
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.CeoDateOfBirth, form),
                                    name: FormField.CeoDateOfBirth,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={values[FormField.CeoDateOfBirth]}
                            onChange={value => {
                                setFieldValue(FormField.CeoDateOfBirth, value);
                                setTimeout(() => setFieldTouched(FormField.CeoDateOfBirth, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingInputControl
                name={FormField.CeoCountryOrigin}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.CeoCountryOrigin] ?? ''}
                        onChange={onAutocompleteChange(FormField.CeoCountryOrigin)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CeoCountryOrigin, form),
                            required: true,
                            name: FormField.CeoCountryOrigin,
                            label: 'Nationality'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CeoAddressLine1}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CeoAddressLine1, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.CeoAddressLine1}
                        value={values[FormField.CeoAddressLine1]}
                        label='Residential Address'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Contact Information
            </Typography>
            <OnboardingInputControl
                name={FormField.CeoEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CeoEmail, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='email'
                        size='small'
                        name={FormField.CeoEmail}
                        value={values[FormField.CeoEmail]}
                        label='E-mail'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CeoPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CeoPhone, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='tel'
                        size='small'
                        name={FormField.CeoPhone}
                        value={values[FormField.CeoPhone]}
                        label='Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.IsPepCeo}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slots={{
                            textField: <></>
                        }}
                        slotProps={{
                            typography: {
                                sx: {
                                    mt: 2
                                },
                                children: 'Is this person PEP or subject to sanctions?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.IsPepCeo,
                                name: FormField.IsPepCeo,
                                value: values[FormField.IsPepCeo],
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h5'
                gutterBottom
            >
                Authorized Signatory
            </Typography>
            <Typography
                variant='h6'
            >
                Upload Passport
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Passport should be in date and within 3 months of expiry
            </Typography>
            <OnboardingInputControl
                name={FormField.AuthSigPassportFileKey}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.AuthSigPassportFileKey}
                        file={values[FormField.AuthSigPassportFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                variant='h6'
            >
                Upload Utility Bill
            </Typography>
            <Typography
                variant='caption'
                gutterBottom
            >
                Dated within the previous 3 months
            </Typography>
            <OnboardingInputControl
                name={FormField.AuthSigUtilityBillFileKey}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <ProofFileInput
                        multiple={false}
                        disabled={disabled}
                        name={FormField.AuthSigUtilityBillFileKey}
                        file={values[FormField.AuthSigUtilityBillFileKey]}
                        setFieldValue={setFieldValue}
                        setFieldTouched={setFieldTouched}
                        placeholderProps={{
                            titleSlot: (
                                <IdentityPlaceholderTitle />
                            )
                        }}
                        accept={acceptRules.identity}
                        maxSize={PROOF_FILE.maxSize}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Personal Information
            </Typography>
            <OnboardingInputControl
                name={FormField.AuthSigName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AuthSigName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.AuthSigName}
                        value={values[FormField.AuthSigName]}
                        label='Full Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.AuthSigDateOfBirth}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            disabled={disabled}
                            maxDate={adultDate()}
                            format={UiDateTimeFormatter.Ui}
                            label='Date of Birth'
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.AuthSigDateOfBirth, form),
                                    name: FormField.AuthSigDateOfBirth,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={values[FormField.AuthSigDateOfBirth]}
                            onChange={value => {
                                setFieldValue(FormField.AuthSigDateOfBirth, value);
                                setTimeout(() => setFieldTouched(FormField.AuthSigDateOfBirth, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <OnboardingInputControl
                name={FormField.AuthSigCountryOrigin}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.AuthSigCountryOrigin] ?? ''}
                        onChange={onAutocompleteChange(FormField.AuthSigCountryOrigin)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.AuthSigCountryOrigin, form),
                            required: true,
                            name: FormField.AuthSigCountryOrigin,
                            label: 'Nationality'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.AuthSigAddressLine1}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AuthSigAddressLine1, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.AuthSigAddressLine1}
                        value={values[FormField.AuthSigAddressLine1]}
                        label='Residential Address'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Contact Information
            </Typography>
            <OnboardingInputControl
                name={FormField.AuthSigEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AuthSigEmail, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='email'
                        size='small'
                        name={FormField.AuthSigEmail}
                        value={values[FormField.AuthSigEmail]}
                        label='E-mail'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.AuthSigPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.AuthSigPhone, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        type='tel'
                        size='small'
                        name={FormField.AuthSigPhone}
                        value={values[FormField.AuthSigPhone]}
                        label='Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.IsPepAuthSig}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        slots={{
                            textField: <></>
                        }}
                        slotProps={{
                            typography: {
                                sx: {
                                    mt: 2
                                },
                                children: 'Is this person PEP or subject to sanctions?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.IsPepAuthSig,
                                name: FormField.IsPepAuthSig,
                                value: values[FormField.IsPepAuthSig],
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h5'
            >
                Shareholder / Beneficial Owner
            </Typography>
            <Typography
                variant='subtitle2'
                color='text.secondary'
            >
                List any owner that has more than 20% ownership
            </Typography>
            <OtherShareholders
                {...onboardingDirectorsAndShareholdersProps}
            />
        </OnboardingForm>
    );
};

export default DirectorsAndShareholders;
